import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { signUpload } from '@/lib/hooks/pagamentos';
import { api, ApiError } from '@/lib/api';
import type { OcrExtractResult } from '@gestaki/shared/schemas';
import type { DocType, OCRResult } from '@/mobile/modulos/scan/types';

// Mapeia DocType local (minúsculo) para o enum esperado pelo backend
const DOC_TYPE_MAP: Record<DocType, 'NFE' | 'RECIBO' | 'BOLETO' | 'EXTRATO'> = {
  nfe: 'NFE',
  recibo: 'RECIBO',
  boleto: 'BOLETO',
  extrato: 'EXTRATO',
};

function dataUrlToBlob(dataUrl: string): Blob {
  const commaIdx = dataUrl.indexOf(',');
  const header = commaIdx >= 0 ? dataUrl.slice(0, commaIdx) : '';
  const b64 = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

function normalizeOcrResult(raw: OcrExtractResult): OCRResult {
  return {
    emitenteCnpj: raw.emitenteCnpj ?? '',
    emitenteNome: raw.emitenteNome ?? '',
    valorTotal: raw.valorTotal ?? 0,
    dataEmissao: raw.dataEmissao ?? new Date().toISOString().slice(0, 10),
    categoria: raw.categoria ?? '',
    formaPagamento: '',
    confianca: raw.confianca,
  };
}

export type OCRStatus = 'idle' | 'running' | 'done' | 'error';

export type OCRErrorKind = 'quota' | 'server' | 'network' | null;

export interface UseOCRScanReturn {
  status: OCRStatus;
  result: OCRResult | null;
  r2Key: string | null;
  errorKind: OCRErrorKind;
  run: (workspaceId: string, docType: DocType, imageDataUrl: string | null) => void;
  reset: () => void;
}

/**
 * Pipeline OCR real (Fase 3.2):
 *   dataURL → blob → signUpload → PUT R2 → POST /ocr/extract → OCRResult
 *
 * Erros tratados:
 *   - 429 → errorKind='quota'  (cota mensal atingida)
 *   - 5xx  → errorKind='server'
 *   - network → errorKind='network'
 */
export function useOCRScan(): UseOCRScanReturn {
  const [status, setStatus] = useState<OCRStatus>('idle');
  const [result, setResult] = useState<OCRResult | null>(null);
  const [r2Key, setR2Key] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<OCRErrorKind>(null);

  const run = useCallback(
    async (workspaceId: string, docType: DocType, imageDataUrl: string | null) => {
      if (!imageDataUrl) {
        toast.error('Nenhuma imagem capturada. Tente novamente.');
        return;
      }

      setStatus('running');
      setResult(null);
      setR2Key(null);
      setErrorKind(null);

      try {
        // 1. dataURL → Blob
        const blob = dataUrlToBlob(imageDataUrl);

        // 2. Assinar upload no R2
        const timestamp = Date.now();
        const signed = await signUpload({
          filename: `scan-${timestamp}.jpg`,
          contentType: 'image/jpeg',
          size: blob.size,
          folder: 'notas-fiscais',
        });

        // 3. PUT no R2 com presigned URL
        const putRes = await fetch(signed.url, {
          method: 'PUT',
          body: blob,
          headers: { 'Content-Type': 'image/jpeg' },
        });
        if (!putRes.ok) {
          throw new ApiError(`Upload R2 falhou (${putRes.status})`, putRes.status);
        }

        // 4. POST /ocr/extract
        const ocrRaw = await api.post<OcrExtractResult>(
          `/v1/workspaces/${workspaceId}/ocr/extract`,
          { r2Key: signed.key, docType: DOC_TYPE_MAP[docType] },
        );

        setR2Key(signed.key);
        setResult(normalizeOcrResult(ocrRaw));
        setStatus('done');
      } catch (err) {
        if (err instanceof ApiError && err.status === 429) {
          setErrorKind('quota');
          toast.error('Cota mensal de OCR atingida — aguarde o próximo mês.');
        } else if (err instanceof TypeError || (err instanceof Error && err.message === 'Failed to fetch')) {
          setErrorKind('network');
          toast.error('Sem conexão. Verifique sua internet.');
        } else {
          setErrorKind('server');
          toast.error('Não conseguimos ler o documento. Tente novamente.');
        }
        setStatus('error');
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setResult(null);
    setR2Key(null);
    setErrorKind(null);
  }, []);

  return { status, result, r2Key, errorKind, run, reset };
}
